import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import SideSheetModal from "@/components/SideSheetModal";
import TextField from "@/components/TextField";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import { ObjectHeadingWrapper } from "@/features/person/components/PersonObjectHeading";
import DateSelect from "@/components/DateSelect";
import { DateValueType } from "react-tailwindcss-datepicker";
import Autocomplete from "@/components/Autocomplete";
import ListItem from "@/components/ListItem/ListItem";
import Chip, { ChipSet } from "@/components/Chip/Chip";
import Banner from "@/components/Banner";
import Switch from "@/components/Switch";
import { showSnackbar } from "@/lib/snackbarUtils";
import { dateValueTypeToISOString } from "../NotesTable/noteHelpers";
import { SOME_USERS_QUERY } from "@/pages/people/queries";
import {
  CREATE_TASK_MUTATION,
  type CreateTaskResponse,
  type BackofficeCRMCreateTaskInput,
  type BackofficeCRMReferenceableInput
} from "./mutations";
import { DEPARTMENTS_QUERY } from "./queries";
import { addWorkdayTeamTag, buildTeamVisibilityOptions } from "./teamVisibilityOptions";
import TeamVisibilityField from "./TeamVisibilityField";
import { QUICK_DUE_DATE_OPTIONS } from "./quickDueDate";
import { privateTaskSupportingText, hasDistinctPrivateAssignee } from "./privateTaskCopy";
import type { BackofficeUser } from "@/hooks/useAttachTask";
import useAmplitude from "@/hooks/useAmplitude";
import { useFlags } from "@/hooks/useFeatureFlags";
import { TaskStageValues, TaskVisibilityValues } from "@/types";
import {
  TASK_ANALYTICS_EVENTS,
  getCreatedTaskAnalyticsData,
  type TaskAnalyticsSource
} from "@/pages/tasks/_utils/amplitude";

export type AddTaskSideSheetProps = {
  open: boolean;
  onClose: () => void;
  onSaveSuccess?: (taskId?: string) => void;
  refetchQueries?: DocumentNode[];
  currentUser?: { id: string; name: string; email: string; department?: string | null };
  defaultReferences?: BackofficeCRMReferenceableInput[];
  defaultPrivate?: boolean;
  skipTeamAutoTag?: boolean;
  pgaId?: string;
  allowClearPersonReference?: boolean;
  personReferencePending?: boolean;
  source?: TaskAnalyticsSource;
};

export default function AddTaskSideSheet({
  open,
  onClose,
  onSaveSuccess,
  refetchQueries = [],
  currentUser,
  defaultReferences,
  defaultPrivate = false,
  skipTeamAutoTag = false,
  pgaId,
  allowClearPersonReference = false,
  personReferencePending = false,
  source = "global_create"
}: AddTaskSideSheetProps) {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const flags = useFlags();
  const teamsEnabled = !!flags["m360-department-task-queue"];
  const privateTeamVisibilityEnabled = !!flags["m360-private-team-task-visibility"];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState<DateValueType>(null);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState<BackofficeUser | null>(null);
  const [selectedTeamTags, setSelectedTeamTags] = useState<string[]>([]);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [isPrivate, setIsPrivate] = useState(defaultPrivate);

  const [selectedChip, setSelectedChip] = useState<number | null>(null);
  const [errors, setErrors] = useState<{
    name?: string;
    assignee?: string;
  }>({});
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [personReferenceCleared, setPersonReferenceCleared] = useState(false);

  const { data: usersData, loading: usersLoading } = useQuery(SOME_USERS_QUERY, {
    variables: { search: debouncedSearchTerm },
    skip: !debouncedSearchTerm
  });

  const { data: departmentsData } = useQuery(DEPARTMENTS_QUERY, {
    skip: !teamsEnabled || !open
  });

  const [createTask, { loading: createLoading }] = useMutation<CreateTaskResponse>(CREATE_TASK_MUTATION, {
    refetchQueries
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(assigneeSearchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeSearchTerm]);

  const prefillCurrentUser = useCallback(() => {
    if (!currentUser) return;

    const assignee: BackofficeUser = {
      id: currentUser.id,
      name: currentUser.name,
      email: currentUser.email,
      department: currentUser.department
    };
    setSelectedAssignee(assignee);
    setAssigneeSearchTerm(currentUser.name);
    if (teamsEnabled && !skipTeamAutoTag) {
      setSelectedTeamTags((prev) => addWorkdayTeamTag(prev, currentUser.department));
    }
  }, [currentUser, teamsEnabled, skipTeamAutoTag]);

  useEffect(() => {
    const oneWeekOut = new Date();
    oneWeekOut.setDate(oneWeekOut.getDate() + 7);

    setName("");
    setDescription("");
    setDueDate({ startDate: oneWeekOut, endDate: oneWeekOut });
    setSelectedChip(7);
    setAssigneeSearchTerm("");
    setDebouncedSearchTerm("");
    setSelectedAssignee(null);
    setSelectedTeamTags([]);
    setTeamSearchTerm("");
    setIsPrivate(defaultPrivate);
    setErrors({});
    setMutationError(null);
    setPersonReferenceCleared(false);
  }, [open, defaultPrivate]);

  const hasPreSelectedPerson = Boolean(pgaId) && !personReferenceCleared;
  const effectiveReferences = useMemo(() => {
    if (!defaultReferences?.length || personReferenceCleared) return undefined;
    return defaultReferences;
  }, [defaultReferences, personReferenceCleared]);

  useEffect(() => {
    if (open) {
      prefillCurrentUser();
    }
  }, [open, prefillCurrentUser]);

  function setIsSideSheetOpen(isOpen: boolean) {
    if (!isOpen) {
      onClose();
    }
  }

  function setQuickDate(daysFromNow: number) {
    if (selectedChip === daysFromNow) {
      setSelectedChip(null);
      setDueDate(null);
      return;
    }
    setSelectedChip(daysFromNow);
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    setDueDate({ startDate: date, endDate: date });
  }

  function selectAssignee(user: BackofficeUser) {
    setSelectedAssignee(user);
    setAssigneeSearchTerm(user.name);
    if (teamsEnabled && !skipTeamAutoTag) {
      setSelectedTeamTags((prev) => addWorkdayTeamTag(prev, user.department));
    }
    if (errors.assignee) {
      setErrors((prev) => ({ ...prev, assignee: undefined }));
    }
  }

  const handleSave = async () => {
    setMutationError(null);

    const newErrors: {
      name?: string;
      assignee?: string;
    } = {};

    if (!name.trim()) {
      newErrors.name = "Task name is required";
    }
    if (!teamsEnabled && !selectedAssignee) {
      newErrors.assignee = "Assignee is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const dueDateValue = dateValueTypeToISOString(dueDate);
    const trimmedName = name.trim();

    const input: BackofficeCRMCreateTaskInput = {
      name: trimmedName,
      description: description.trim(),
      visibility: isPrivate ? TaskVisibilityValues.PRIVATE : TaskVisibilityValues.PUBLIC
    };

    if (selectedAssignee) {
      input.assignedToId = selectedAssignee.id;
    }

    if (teamsEnabled && selectedTeamTags.length > 0) {
      input.teamTags = selectedTeamTags;
    }

    if (dueDateValue) {
      input.dueDate = dueDateValue;
    }

    if (effectiveReferences && effectiveReferences.length > 0) {
      input.references = effectiveReferences;
    }

    try {
      const result = await createTask({ variables: { input } });

      if (result.data?.createTask?.success) {
        const taskId = result.data.createTask.task?.id;
        trackEvent(
          TASK_ANALYTICS_EVENTS.created,
          getCreatedTaskAnalyticsData({
            taskId,
            source,
            stage: TaskStageValues.OPEN,
            visibility: input.visibility,
            dueDate: dueDateValue,
            referenceTypes: effectiveReferences?.map((reference) => reference.referenceableType),
            hasAssignee: Boolean(selectedAssignee?.id),
            teamTagCount: teamsEnabled ? selectedTeamTags.length : 0
          })
        );
        showSnackbar("Task created", {
          actions: taskId
            ? [
                <Button key="view" variant="text" onClick={() => navigate(`/tasks/${taskId}`)}>
                  View Task
                </Button>
              ]
            : undefined
        });
        onSaveSuccess?.(taskId);
        onClose();
      } else {
        setMutationError(result.data?.createTask?.message || "Failed to create task");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error creating task. Please try again.";
      setMutationError(errorMessage);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const availableDepartmentOptions = buildTeamVisibilityOptions(
    departmentsData?.departments || [],
    selectedTeamTags,
    teamSearchTerm
  );

  function handleTeamVisibilitySelect(option: { value: string; label: string }) {
    setSelectedTeamTags((prev) => addWorkdayTeamTag(prev, option.value));
    setTeamSearchTerm("");
  }

  function removeTeamTag(department: string) {
    setSelectedTeamTags((prev) => prev.filter((tag) => tag !== department));
  }

  return (
    <SideSheetModal
      isSidebarOpen={open}
      setSidebarOpen={setIsSideSheetOpen}
      title="Add Task"
      actions={
        <>
          <Button variant="text" onClick={handleCancel} disabled={createLoading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={createLoading || (personReferencePending && !personReferenceCleared)}
            icon="check"
          >
            Save
          </Button>
        </>
      }
    >
      <div className={`space-y-6 ${hasPreSelectedPerson ? "mt-2" : "mt-6"}`}>
        {mutationError && (
          <Banner variant="error" description={mutationError} inline closable={false} hideSecondaryDismiss />
        )}

        {hasPreSelectedPerson && pgaId && (
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <ObjectHeadingWrapper
                pgaId={pgaId}
                variant="condensed"
                showMinimalMetadata={true}
                hideTags={true}
                avatarSize="medium"
              />
            </div>
            {allowClearPersonReference && (
              <button
                type="button"
                className="cursor-pointer flex items-center shrink-0 p-1 rounded-full hover:bg-sys-brand-surface-container-high transition-colors"
                onClick={() => setPersonReferenceCleared(true)}
                aria-label="Remove selected person"
                disabled={createLoading}
              >
                <Icon name="close" size={20} />
              </button>
            )}
          </div>
        )}

        <TextField
          label="Task Name"
          value={name}
          onChangeText={setName}
          required
          error={!!errors.name}
          errorText={errors.name}
          className="w-full"
        />

        <Autocomplete<BackofficeUser>
          label="Assignee"
          required={!teamsEnabled}
          disabled={!teamsEnabled}
          supportingText={teamsEnabled ? undefined : "Assignee editing coming at a later date"}
          query={selectedAssignee ? selectedAssignee.name : assigneeSearchTerm}
          loadingResults={usersLoading}
          data={usersData?.backofficeUsers || []}
          onChangeText={(value) => {
            if (selectedAssignee) {
              setSelectedAssignee(null);
            }
            setAssigneeSearchTerm(value);
            if (errors.assignee) {
              setErrors((prev) => ({ ...prev, assignee: undefined }));
            }
          }}
          reset={() => {
            setSelectedAssignee(null);
            setAssigneeSearchTerm("");
            setDebouncedSearchTerm("");
          }}
          onSelect={selectAssignee}
          renderItem={(user: BackofficeUser) => (
            <ListItem compact useAvatar iconName="person" headline={user.name} supportingText={user.email} />
          )}
          error={!!errors.assignee}
          errorText={errors.assignee}
        />

        {teamsEnabled && (
          <TeamVisibilityField
            selectedTeamTags={selectedTeamTags}
            teamSearchTerm={teamSearchTerm}
            onTeamSearchChange={setTeamSearchTerm}
            availableDepartmentOptions={availableDepartmentOptions}
            onSelect={handleTeamVisibilitySelect}
            onRemove={removeTeamTag}
            privateTeamVisibilityEnabled={privateTeamVisibilityEnabled}
            data-testid="team-visibility-autocomplete"
          />
        )}

        <TextField
          label="Task Description"
          type="textarea"
          rows={6}
          value={description}
          onChangeText={setDescription}
          className="w-full"
        />

        <div className="space-y-3">
          <DateSelect
            label="Due Date"
            value={dueDate}
            onChange={(date) => {
              setDueDate(typeof date === "string" ? null : date);
              setSelectedChip(null);
            }}
            placeholder="Select a date"
            useSingleDate
            popoverDirection="down"
            className="w-full"
          />
          <ChipSet>
            {QUICK_DUE_DATE_OPTIONS.map((option) => (
              <Chip
                key={option.days}
                label={option.label}
                showCheckmark={false}
                selected={selectedChip === option.days}
                onClick={() => setQuickDate(option.days)}
              />
            ))}
          </ChipSet>
        </div>

        <Switch
          disabled={createLoading}
          headline="Make Private"
          selected={isPrivate}
          supportingText={privateTaskSupportingText({
            hasDistinctAssignee: hasDistinctPrivateAssignee(selectedAssignee?.id, currentUser?.id),
            privateTeamVisibilityEnabled,
            hasSelectedTeams: selectedTeamTags.length > 0
          })}
          onChange={() => setIsPrivate(!isPrivate)}
        />
      </div>
    </SideSheetModal>
  );
}
